/**
 * Live Quiz Host WebSocket Hook
 *
 * Manages WebSocket connection and game control for the quiz host.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useHostStore } from '../stores/liveQuizStore';
import type { ServerMessage, ClientMessage } from '../lib/liveQuizProtocol';

interface UseLiveQuizHostOptions {
  sessionId: string;
  hostUserId: string;
  onError?: (error: string) => void;
}

export function useLiveQuizHost({ sessionId, hostUserId, onError }: UseLiveQuizHostOptions) {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);

  const {
    connected,
    connecting,
    error,
    sessionId: storeSessionId,
    gamePin,
    status,
    settings,
    currentQuestionIndex,
    totalQuestions,
    currentQuestion,
    timeRemaining,
    participants,
    readyCount,
    answerStats,
    leaderboard,
    answeredCount,
    correctAnswer,
    setConnected,
    setConnecting,
    setError,
    setSession,
    setStatus,
    setQuestion,
    setTimeRemaining,
    setParticipants,
    addParticipant,
    removeParticipant,
    updateParticipantReady,
    setAnswerStats,
    setLeaderboard,
    setAnsweredCount,
    setCorrectAnswer,
    reset,
  } = useHostStore();

  // Handle incoming messages
  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const message = JSON.parse(event.data) as ServerMessage;
      console.log('[Host WS] Received:', message.type, message);

      switch (message.type) {
        case 'welcome':
          setSession(
            message.sessionId,
            message.gamePin,
            message.settings,
            message.totalQuestions
          );
          setStatus(message.status);
          setParticipants(message.participants);
          break;

        case 'player_joined':
          addParticipant(message.participant);
          break;

        case 'player_left':
          removeParticipant(message.participantId);
          break;

        case 'player_ready_status':
          updateParticipantReady(
            message.participantId,
            message.isReady,
            message.readyCount
          );
          break;

        case 'game_starting':
          setStatus('starting');
          break;

        case 'question_start':
          setStatus('question');
          setQuestion(
            message.question as any, // Type assertion needed due to omitted correctAnswer
            message.index,
            message.timeSeconds
          );
          break;

        case 'time_update':
          setTimeRemaining(message.remaining);
          break;

        case 'answer_count_update':
          setAnsweredCount(message.answeredCount);
          break;

        case 'answer_stats':
          setAnswerStats(message.stats);
          break;

        case 'question_end':
          setStatus('reveal');
          setAnswerStats(message.stats);
          if (message.correctAnswer !== undefined) {
            setCorrectAnswer(message.correctAnswer);
          }
          break;

        case 'leaderboard':
          setStatus('leaderboard');
          setLeaderboard(message.entries);
          break;

        case 'game_paused':
          setStatus('paused');
          break;

        case 'game_resumed':
          setStatus('question');
          setTimeRemaining(message.timeRemaining);
          break;

        case 'game_ended':
          setStatus('ended');
          setLeaderboard(message.finalLeaderboard);
          break;

        case 'status_change':
          setStatus(message.status);
          break;

        case 'error':
          setError(message.message);
          onError?.(message.message);
          break;

        case 'pong':
          // Heartbeat response
          break;
      }
    } catch (error) {
      console.error('Failed to parse message:', error);
    }
  }, [
    setSession,
    setStatus,
    setParticipants,
    addParticipant,
    removeParticipant,
    updateParticipantReady,
    setQuestion,
    setTimeRemaining,
    setAnsweredCount,
    setAnswerStats,
    setLeaderboard,
    setCorrectAnswer,
    setError,
    onError,
  ]);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    setConnecting(true);
    setError(null);

    // In production, connect directly to the live-quiz-worker
    // In development, use the local proxy
    const isProduction = window.location.hostname.includes('.pages.dev') ||
                         window.location.hostname.includes('.workers.dev') ||
                         !window.location.hostname.includes('localhost');

    let wsUrl: string;
    if (isProduction) {
      // Direct connection to live-quiz-worker in production
      wsUrl = `wss://live-quiz-worker.ziv-ba4.workers.dev/session/${sessionId}/?role=host&hostUserId=${hostUserId}`;
    } else {
      // Local development proxy
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${protocol}//${window.location.host}/api/live-quiz/ws/${sessionId}?role=host&hostUserId=${hostUserId}`;
    }

    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      setConnected(true);
      reconnectAttemptsRef.current = 0;
    };

    ws.onclose = (event) => {
      setConnected(false);

      // Attempt reconnection for non-clean closes
      if (!event.wasClean && reconnectAttemptsRef.current < 5) {
        reconnectAttemptsRef.current++;
        const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);

        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, delay);
      }
    };

    ws.onerror = () => {
      setError('Connection error');
    };

    ws.onmessage = handleMessage;

    wsRef.current = ws;
  }, [sessionId, hostUserId, setConnecting, setConnected, setError, handleMessage]);

  // Disconnect
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    reset();
  }, [reset]);

  // Send message
  const send = useCallback((message: ClientMessage) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  // Game controls
  const startGame = useCallback(() => {
    send({ type: 'host:start_game' });
  }, [send]);

  const nextQuestion = useCallback(() => {
    send({ type: 'host:next_question' });
  }, [send]);

  const revealAnswer = useCallback(() => {
    send({ type: 'host:reveal_answer' });
  }, [send]);

  const showLeaderboard = useCallback(() => {
    send({ type: 'host:show_leaderboard' });
  }, [send]);

  const pauseGame = useCallback(() => {
    send({ type: 'host:pause_game' });
  }, [send]);

  const resumeGame = useCallback(() => {
    send({ type: 'host:resume_game' });
  }, [send]);

  const endGame = useCallback(() => {
    send({ type: 'host:end_game' });
  }, [send]);

  const kickPlayer = useCallback((participantId: string, reason?: string) => {
    send({ type: 'host:kick_player', participantId, reason });
  }, [send]);

  const skipQuestion = useCallback(() => {
    send({ type: 'host:skip_question' });
  }, [send]);

  // Heartbeat
  useEffect(() => {
    if (!connected) return;

    const interval = setInterval(() => {
      send({ type: 'ping' });
    }, 30000);

    return () => clearInterval(interval);
  }, [connected, send]);

  // Connect on mount
  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  return {
    // Connection state
    connected,
    connecting,
    error,

    // Session info
    sessionId: storeSessionId,
    gamePin,
    status,
    settings,

    // Question state
    currentQuestionIndex,
    totalQuestions,
    currentQuestion,
    timeRemaining,

    // Participants
    participants,
    readyCount,
    activeParticipants: participants.filter((p) => p.status === 'connected'),

    // Stats
    answerStats,
    leaderboard,
    answeredCount,
    correctAnswer,

    // Actions
    startGame,
    nextQuestion,
    revealAnswer,
    showLeaderboard,
    pauseGame,
    resumeGame,
    endGame,
    kickPlayer,
    skipQuestion,
    reconnect: connect,
    disconnect,
  };
}

export default useLiveQuizHost;
